/**
 * Os dias da viagem, como o servidor os tem — e como o aparelho os guardou.
 *
 * A LISTA de datas o cliente sabe derivar (é o intervalo da viagem). O que só
 * o servidor sabe é o `id` de cada dia — necessário para criar atividade e
 * para abrir o dia — e quanto já foi planejado.
 *
 * Por isso este módulo **guarda os dias no aparelho**. Sem isso, o roteiro
 * seria uma tela que só funciona com rede: offline, a data da URL não teria
 * como virar um `dayId`, e o dia não abriria — nem com tudo já em cache. É o
 * mesmo motivo pelo qual o working set da viagem é persistido, e a mesma
 * ordem: o que está no aparelho vale primeiro, a rede refina depois.
 */

import type { DayCategory, DayColor } from '@nv/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { localDb } from '../features/roteiro/persistence';

export interface DiaDaViagem {
  id: string;
  /** `YYYY-MM-DD`. */
  date: string;
  title: string | null;
  destination: string | null;
  /** Tema e cor do dia (item 28). Registros antigos no aparelho não os têm. */
  category?: DayCategory | null;
  color?: DayColor | null;
  activityCount: number;
  /** Horários das atividades, para "Manhã / Tarde" sem abrir o dia. */
  startTimes?: string[];
}

/** Edita título, destino, tema e cor do dia. Devolve o dia como ficou. */
export async function updateTripDay(tripId: string, dayId: string, patch: Partial<Pick<DiaDaViagem, 'title' | 'destination' | 'category' | 'color'>>): Promise<DiaDaViagem> {
  const res = await fetch(`/api/v1/trips/${tripId}/days/${dayId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const erro = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(erro.message ?? `Não foi possível salvar o dia (${res.status}).`);
  }
  return (await res.json()) as DiaDaViagem;
}

const chave = (tripId: string) => `trip:days:${tripId}`;

export async function loadDays(tripId: string): Promise<DiaDaViagem[] | null> {
  if (!tripId) return null;
  const db = await localDb();
  return (await db.cache.get<DiaDaViagem[]>(chave(tripId))) ?? null;
}

export async function saveDays(tripId: string, days: DiaDaViagem[]): Promise<void> {
  if (!tripId) return;
  const db = await localDb();
  await db.cache.set(chave(tripId), days);
}

export async function listTripDays(tripId: string): Promise<{ days: DiaDaViagem[] }> {
  const res = await fetch(`/api/v1/trips/${tripId}/days`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Falha ao carregar os dias (${res.status})`);
  return (await res.json()) as { days: DiaDaViagem[] };
}

export interface DiasDaViagem {
  days: DiaDaViagem[] | null;
  carregando: boolean;
  /** A rede falhou E não havia nada no aparelho. */
  erro: boolean;
}

export function useDiasDaViagem(tripId: string): DiasDaViagem {
  // `undefined` = ainda lendo o IndexedDB; `null` = leu e não havia nada.
  const [local, setLocal] = useState<DiaDaViagem[] | null | undefined>(undefined);

  useEffect(() => {
    let vivo = true;
    void loadDays(tripId).then((dados) => {
      if (vivo) setLocal(dados);
    });
    return () => {
      vivo = false;
    };
  }, [tripId]);

  const daRede = useQuery({
    queryKey: ['viagem', 'dias', tripId],
    queryFn: () => listTripDays(tripId),
    enabled: tripId.length > 0,
  });

  const dias = daRede.data?.days ?? null;

  useEffect(() => {
    if (dias) void saveDays(tripId, dias);
    // Só quando a resposta da rede muda de verdade.
  }, [tripId, JSON.stringify(dias)]);

  const efetivo = dias ?? local ?? null;

  return {
    days: efetivo,
    carregando: efetivo === null && (local === undefined || daRede.isLoading),
    // Só é erro quando a rede falhou E o aparelho não tinha nada: quem já
    // abriu a viagem antes continua com o roteiro sem rede.
    erro: efetivo === null && local === null && daRede.isError,
  };
}
